import { chainDefiSectionMapping } from "@constants/common";
import { DeFiRow } from "@components/V2/Vault/Common/DefiTypes";
import { useMemo } from "react";
import useDappChain from "./useDappChain";
import defiJSON from "@data/defi.json";

enum ASSETS {
  AGETH = "ageth",
  HGETH = "hgeth",
  RSETH = "rseth",
}

export enum DEFI_FILTER_VARIANT {
  SUCCESS_MODAL = "SUCCESS_MODAL",
  AGETH_VAULT_PARTNERSHIPS = "AGETH_VAULT_PARTNERSHIPS",
  HGETH_VAULT_PARTNERSHIPS = "HGETH_VAULT_PARTNERSHIPS",
  SDUSD_VAULT_PARTNERSHIPS = "SDUSD_VAULT_PARTNERSHIPS",
}
enum FIELD_TYPE {
  RSETH_DEPOSIT_FEATURED = "rsethDepositFeatured",
  AGETH_DEPOSIT_FEATURED = "agethDepositFeatured",
  AGETH_PARTNERSHIPS = "agethPartnerships",
  HGETH_PARTNERSHIPS = "hgethPartnerships",
  RSETH_PARTNERSHIPS = "rsethPartnerships",
}

const getFilteredRows = (
  rows: DeFiRow[],
  field: FIELD_TYPE,
  variant: DEFI_FILTER_VARIANT
) => {
  return rows?.filter((row) => {
    if (variant === DEFI_FILTER_VARIANT.SUCCESS_MODAL) {
      return row[field]?.toLowerCase() === "yes";
    } else if (variant === DEFI_FILTER_VARIANT.AGETH_VAULT_PARTNERSHIPS) {
      return row["Token 1 Icon"].toLowerCase() === ASSETS.AGETH;
    } else if (variant === DEFI_FILTER_VARIANT.HGETH_VAULT_PARTNERSHIPS) {
      return row["Token 1 Icon"].toLowerCase() === ASSETS.HGETH;
    } else if (variant === DEFI_FILTER_VARIANT.SDUSD_VAULT_PARTNERSHIPS) {
      return row["Token 1 Icon"].toLowerCase() === ASSETS.RSETH;
    }
  });
};

interface UseDefiDataProps {
  type: string;
  variant?: DEFI_FILTER_VARIANT;
}

const useDefiData = ({
  type,
  variant = DEFI_FILTER_VARIANT.SUCCESS_MODAL,
}: UseDefiDataProps) => {
  const { chain } = useDappChain();
  const { pools } = defiJSON;

  const sections = useMemo(() => {
    const sections: { [key: string]: DeFiRow[] } = {};
    pools?.data.forEach((row: any) => {
      if (row.Section) {
        sections[row.Section] = sections[row.Section] || [];
        sections[row.Section].push(row);
      }
    });
    return sections;
  }, [pools?.data]);

  // Return empty if variant is not present
  if (!variant) {
    return [];
  }

  // Simplified field type mapping using object
  const fieldTypeMap = {
    [DEFI_FILTER_VARIANT.AGETH_VAULT_PARTNERSHIPS]:
      FIELD_TYPE.AGETH_PARTNERSHIPS,
    [DEFI_FILTER_VARIANT.HGETH_VAULT_PARTNERSHIPS]:
      FIELD_TYPE.HGETH_PARTNERSHIPS,
    [DEFI_FILTER_VARIANT.SDUSD_VAULT_PARTNERSHIPS]:
      FIELD_TYPE.RSETH_PARTNERSHIPS,
    [DEFI_FILTER_VARIANT.SUCCESS_MODAL]:
      type === "restake"
        ? FIELD_TYPE.RSETH_DEPOSIT_FEATURED
        : FIELD_TYPE.HGETH_PARTNERSHIPS,
  };

  const fieldType = fieldTypeMap[variant];

  const rows = getFilteredRows(
    sections[chainDefiSectionMapping[chain.id]],
    fieldType,
    variant
  );

  if (variant === DEFI_FILTER_VARIANT.SUCCESS_MODAL) {
    return rows?.length > 0 ? rows[0] : null;
  }

  return rows?.length > 0 ? rows : [];
};

export default useDefiData;
